import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { Box, Button, MenuItem, TextField } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { QueryProvinces } from "api/general";
import { GetRouteByProvince, GetRouteList } from "api/routes";
import { StoreCustomer } from "api/customers";
import { GetAgentList } from "api/agent";
import { getAllCustomerGroups } from "api/customerGroup";
import { playAlertSound, playCorrectSound, playIncorrectSound } from "utils";
import { cleanLocalNumber } from "utils/phoneValidation";
import { user } from "localStorage";
import ForwardAddressList from "components/ForwardAddressList"; // Import the new component
import DeliveryAddressList from "components/DeliveryAddressList";
import BentoCard from "components/bento/BentoCard";
import { sectionLabelSx, fieldSx, accentFieldSx, ToggleCard } from "components/bento/BentoFormControls";
import { BG_TINT, DIVIDER, PURPLE_DARK } from "components/bento/bentoTokens";

const NewCustomer = () => {
  const { t } = useTranslation();

  const [provinces, setProvinces] = useState([]);
  const [provinceId, setProvinceId] = useState("");
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [remark, setRemark] = useState("");
  const [routeId, setRouteId] = useState("");
  const [routeList, setRouteList] = useState([]);
  const [isCOD, setIsCOD] = useState(false);
  const [isDeliveryRequired, setIsDeliveryRequired] = useState(false);
  const [isSubCustomer, setIsSubCustomer] = useState(false);
  const [level] = useState("1");
  const [agentList, setAgentList] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [isForwardRequired, setIsForwardRequired] = useState(false);
  const [isSelfOrder, setIsSelfOrder] = useState(false);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [forwardAddresses, setForwardAddresses] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [groupId, setGroupId] = useState("");

  const validflag = true;
  const _user = user();
  const isAgentUser = _user.role === "agent";

  useEffect(() => {
    QueryProvinces().then((res) => setProvinces(res.data));
    GetRouteList().then((res) => {
      setRouteList(res.data);
    });
    // Load customer groups
    getAllCustomerGroups().then((result) => {
      if (result.success) {
        const activeGroups = result.data.filter(group => group.isActive);
        setCustomerGroups(activeGroups);
        // Let user choose customer group manually - no auto-selection
      }
    });
  }, []);

  useEffect(() => {
    if (provinceId) {
      if (!isAgentUser) {
        GetRouteByProvince(provinceId).then((res) => {
          console.log(res.data);
          if (res.data) {
            setRouteId(res.data);
          }
        });
      }
    }
  }, [provinceId, isAgentUser]);

  useEffect(() => {
    if (!isSubCustomer) {
      setAgentId("");
    }
  }, [isSubCustomer]);

  useEffect(() => {
    if (isSubCustomer) {
      GetAgentList().then((res) => {
        if (res.data) {
          setAgentList(res.data);
        }
      });
    }
  }, [isSubCustomer]);

  function clearForm() {
    setProvinceId("");
    setRouteId("");
    setName("");
    setWhatsapp("");
    setRemark("");
    setAgentList([]);
    setAgentId("");
    setIsCOD(false);
    setIsDeliveryRequired(false);
    setIsForwardRequired(false);
    setIsSubCustomer(false);
    setIsSelfOrder(false);
    // Clear customer group selection - let user choose
    setGroupId("");
  }

  const formSubmittedHandler = (e) => {
    e.preventDefault();

    const provinceObj = provinces.find(
      (item) => item.pr_id === provinceId.toString()
    );

    if (!groupId) {
      playAlertSound();
      Swal.fire({
        title: t("newCustomer.customerGroupRequiredTitle"),
        icon: "warning",
        text: t("newCustomer.customerGroupRequiredText"),
      });
      return;
    }

    if (isDeliveryRequired && deliveryAddresses.length <= 0) {
      playAlertSound();
      Swal.fire({
        title: t("newCustomer.locationRequiredTitle"),
        icon: "warning",
        text: t("newCustomer.locationRequiredText"),
      });
      return;
    }

    if (isForwardRequired && forwardAddresses.length <= 0) {
      playAlertSound();
      Swal.fire({
        title: t("newCustomer.forwardRequiredTitle"),
        icon: "warning",
        text: t("newCustomer.forwardRequiredText"),
      });
      return;
    }
    StoreCustomer(
      name,
      whatsapp,
      provinceObj,
      remark,
      routeId,
      validflag,
      isCOD,
      isDeliveryRequired,
      isForwardRequired,
      isAgentUser? true : isSubCustomer,
      isAgentUser? _user.agentId : agentId,
      level,
      isSelfOrder,
      deliveryAddresses,
      forwardAddresses,
      groupId
    ).then((res) => {
      console.log(res);
      if (res && res.success) {
        clearForm();
        playCorrectSound();
        Swal.fire({
          title: t("newCustomer.createdTitle"),
          icon: "success",
          text: t("newCustomer.createdText", { name }),
          timer: 1500,
        });
      } else if (res.status === 400) {
        playIncorrectSound();
        Swal.fire({
          title: t("newCustomer.errorTitle"),
          icon: "error",
          text: `Status ${res.status} - ${res.data.message}`,
          showConfirmButton: true,
        });
      } else {
        playIncorrectSound();
        Swal.fire({
          icon: "error",
          text: res.message,
          showConfirmButton: true,
        });
      }
    });
  };

  return (
    <form onSubmit={formSubmittedHandler}>
      <BentoCard sx={{ maxWidth: "860px" }} bodySx={{ p: 0 }}>

        {/* Section 1: Customer Info */}
        <Box sx={{ p: "24px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
          <Box sx={sectionLabelSx}>{t("newCustomer.sections.customerInfo")}</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px", mb: "16px" }}>
            <TextField
              label={t("newCustomer.name")}
              id="name"
              value={name}
              required
              sx={fieldSx}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              type="number"
              label={t("newCustomer.whatsapp")}
              id="whatsapp"
              value={whatsapp}
              required
              helperText={t("newCustomer.whatsappExample")}
              sx={fieldSx}
              onChange={(e) => setWhatsapp(cleanLocalNumber(e.target.value))}
            />
          </Box>

          <TextField
            select
            fullWidth
            label={t("newCustomer.customerGroup")}
            id="customerGroup"
            value={groupId}
            required
            helperText={t("newCustomer.customerGroupHelper")}
            sx={{ ...fieldSx, mb: "16px" }}
            onChange={(event) => setGroupId(event.target.value)}
          >
            {customerGroups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name} - {group.whatsappAPI}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px", mb: "16px" }}>
            <TextField
              select
              label={t("newCustomer.province")}
              id="province"
              value={!provinceId ? "" : provinceId}
              required
              sx={fieldSx}
              onChange={(event) => setProvinceId(event.target.value)}
            >
              {provinces &&
                provinces.map(({ pr_id, pr_name, pr_name_en }) => (
                  <MenuItem key={pr_id} value={pr_id}>
                    {pr_name} / {pr_name_en}
                  </MenuItem>
                ))}
            </TextField>

            <TextField
              select
              label={t("newCustomer.route")}
              id="route"
              value={!routeId ? "" : routeId}
              required
              sx={fieldSx}
              onChange={(event) => setRouteId(event.target.value)}
            >
              {routeList &&
                routeList.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {id} ({name})
                  </MenuItem>
                ))}
            </TextField>
          </Box>

          <TextField
            fullWidth
            multiline
            maxRows={5}
            label={t("newCustomer.remark")}
            id="remark"
            value={remark}
            sx={fieldSx}
            onChange={(e) => setRemark(e.target.value)}
          />
        </Box>

        {/* Section 2: Delivery Options */}
        <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
          <Box sx={sectionLabelSx}>{t("newCustomer.sections.deliveryOptions")}</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "12px", mb: "12px" }}>
            <ToggleCard
              label={t("newCustomer.cod")}
              checked={isCOD}
              onChange={() => setIsCOD(!isCOD)}
            />
            <ToggleCard
              label={t("newCustomer.selfOrder")}
              checked={isSelfOrder}
              onChange={() => setIsSelfOrder(!isSelfOrder)}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <ToggleCard
              label={t("newCustomer.homeDelivery")}
              checked={isDeliveryRequired}
              onChange={() => setIsDeliveryRequired(!isDeliveryRequired)}
            >
              {isDeliveryRequired && (
                <DeliveryAddressList
                  deliveryAddresses={deliveryAddresses}
                  setDeliveryAddresses={setDeliveryAddresses}
                  isDeliveryRequired={isDeliveryRequired}
                />
              )}
            </ToggleCard>

            <ToggleCard
              label={t("newCustomer.forwardDelivery")}
              checked={isForwardRequired}
              onChange={() => setIsForwardRequired(!isForwardRequired)}
            >
              {isForwardRequired && (
                <ForwardAddressList
                  forwardAddresses={forwardAddresses}
                  setForwardAddresses={setForwardAddresses}
                  isForwardRequired={isForwardRequired}
                />
              )}
            </ToggleCard>
          </Box>
        </Box>

        {/* Section 3: Agent */}
        {!isAgentUser && (
          <Box sx={{ p: "22px 28px 24px" }}>
            <Box sx={sectionLabelSx}>{t("newCustomer.sections.agent")}</Box>
            <ToggleCard
              label={t("newCustomer.isSubCustomer")}
              checked={isSubCustomer}
              onChange={() => setIsSubCustomer(!isSubCustomer)}
            >
              {isSubCustomer && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("newCustomer.selectAgent")}
                  id="agent"
                  value={!agentId ? "" : agentId}
                  required
                  sx={accentFieldSx}
                  onChange={(event) => setAgentId(event.target.value)}
                >
                  {agentList &&
                    agentList.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {id} - {name}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </ToggleCard>
          </Box>
        )}

        {/* Footer: Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: "20px 28px", background: BG_TINT }}>
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            disabled={!provinceId || !routeId}
            startIcon={<CheckIcon fontSize="small" />}
            sx={{
              background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`,
              boxShadow: "0 4px 16px -4px rgba(109,40,217,.55)",
              "&:hover": { background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`, opacity: 0.92 },
            }}
          >
            {t("newCustomer.submit")}
          </Button>
        </Box>

      </BentoCard>
    </form>
  );
};

export default NewCustomer;
